import { Field, FieldLabelText, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import {
  WorksheetBatchUpdateAllTestsTypeId,
  WorksheetBatchUpdateWorksheetField,
  getWorksheetBatchUpdateDefinition,
} from 'Components/WorksheetBatchUpdate';
import {
  WorksheetCollatedMessagesButton,
  WorksheetGrid,
  WorksheetLabelByTypeInjectedPropsProvider,
  WorksheetLink,
  WorksheetLockedStatusIcon,
} from 'Components/Worksheets/Core';
import { getMessagesForMultiplePaths } from 'Components/Worksheets/Core/Helpers/messageHelpers';
import { hasNonEmptyValue } from 'Lib/Any';
import deepEquals from 'Lib/Object/deepEquals';
import { pickBy } from 'ramda';
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { WorksheetBatchUpdateStatusDisplayField } from '../SpecialisedFieldComponents';
import { WorksheetBatchUpdateSelectAllCheckbox } from './WorksheetBatchUpdateSelectAllCheckbox';
import { WorksheetBatchUpdateSelectWorksheetCheckbox } from './WorksheetBatchUpdateSelectWorksheetCheckbox';
import { WorksheetBatchUpdateSelectionGridInjectedPropsProvider } from './WorksheetBatchUpdateSelectionGridInjectedPropsProvider';
import { WorksheetBatchUpdateUpdateFailureIcon } from './WorksheetBatchUpdateUpdateFailureIcon';

/**
 * Provides a grid to select worksheets for batch update,
 * where each row shows a set of fields from the worksheet
 * as determined by the batch update page configuration
 */
let WorksheetBatchUpdateSelectionGrid: React.FC = () => {
  const typeId = useSelector((state) => state.worksheetBatchUpdate.typeId);
  const fields = useSelector((state) => state.worksheetBatchUpdate.fields, deepEquals);

  const worksheetIds = useSelector((state) => Object.keys(state.worksheetBatchUpdate.worksheets), shallowEqual);
  const anyWorksheetLocked = useSelector((state) =>
    Object.values(state.worksheetBatchUpdate.worksheets).some((w) => w.status.readonly.locked)
  );

  const displayedFields = pickBy<
    { [name: string]: WorksheetBatchUpdateWorksheetField },
    { [name: string]: WorksheetBatchUpdateWorksheetField }
  >((_, k) => fields[k].display ?? false, typeId ? getWorksheetBatchUpdateDefinition(typeId!).worksheetFields : {});

  const messagePaths = Object.keys(displayedFields);

  const anyWorksheetHasMessages = useSelector((state) =>
    Object.values(state.worksheetBatchUpdate.worksheets).some(
      (w) => getMessagesForMultiplePaths(w.syncModel_synced, messagePaths).length > 0
    )
  );

  const anyWorksheetHasUpdateFailures = useSelector((state) =>
    Object.keys(state.worksheetBatchUpdate.worksheets).some((w) =>
      hasNonEmptyValue(state.worksheetBatchUpdate.updateFailures[w])
    )
  );

  return (
    <WorksheetGrid
      identifierColumns={[
        {
          component: (worksheetId) => <WorksheetBatchUpdateSelectWorksheetCheckbox worksheetId={worksheetId} />,
          header: <WorksheetBatchUpdateSelectAllCheckbox />,
          width: 30,
        },
        {
          component: <Field input={<TextOutput />} name={`header.Sample.SampleId`} />,
          header: 'Sample ID',
          width: 160,
        },
        {
          component: <WorksheetBatchUpdateStatusDisplayField />,
          header: '',
          width: 24,
          additionalCellStyle: { padding: '0px' },
        },
        {
          component: (worksheetId) => (
            <WorksheetLink
              worksheetPath={`worksheetBatchUpdate.worksheets[${worksheetId}]`}
              shortNameOnly={typeId !== WorksheetBatchUpdateAllTestsTypeId}
              openInNewTab
            />
          ),
          header: 'Worksheet',
          width: typeId === WorksheetBatchUpdateAllTestsTypeId ? 300 : 200,
          hidden: window.qest.isFrame,
        },
        {
          component: <Field input={<TextOutput />} name={`type.Caption`} />,
          header: 'Worksheet',
          width: 300,
          hidden: !window.qest.isFrame || typeId !== WorksheetBatchUpdateAllTestsTypeId,
        },
        {
          component: (worksheetId) => (
            <WorksheetLockedStatusIcon
              worksheetPath={`worksheetBatchUpdate.worksheets[${worksheetId}]`}
              size={'1.25em'}
            />
          ),
          header: '',
          width: 20,
          hidden: !anyWorksheetLocked,
        },
        {
          component: (
            <WorksheetCollatedMessagesButton iconSize={'1.25em'} messagePaths={Object.keys(displayedFields)} />
          ),
          header: '',
          width: 20,
          hidden: !anyWorksheetHasMessages,
        },
        {
          component: <WorksheetBatchUpdateUpdateFailureIcon iconSize={'1.25em'} />,
          header: '',
          width: 20,
          hidden: !anyWorksheetHasUpdateFailures,
        },
      ]}
      fieldColumns={[
        ...Object.entries(displayedFields).map(([key, f]) => ({
          component: React.cloneElement(f.displayComponent!, {
            name: key,
          }),
          width: f.gridWidth ?? 140,
          header: f.gridLabel ?? <FieldLabelText name={key} />,
        })),
        // Always show Checked By and Date Checked at the end
        {
          component: <Field input={<TextOutput />} name={`header.CheckedBy.Name`} />,
          header: 'Checked By',
          width: 200,
        },
        {
          component: <Field input={<TextOutput />} name={`header.CheckedDate`} />,
          header: 'Date Checked',
          width: 120,
        },
      ]}
      worksheetIds={worksheetIds}
      HeaderPropsProvider={({ children }) => (
        <WorksheetLabelByTypeInjectedPropsProvider children={children} typeId={typeId!} />
      )}
      WorksheetPropsProvider={WorksheetBatchUpdateSelectionGridInjectedPropsProvider}
    />
  );
};

WorksheetBatchUpdateSelectionGrid = withInjectedProps(WorksheetBatchUpdateSelectionGrid);

export default WorksheetBatchUpdateSelectionGrid;
export { WorksheetBatchUpdateSelectionGrid };
